import React, { useState, useEffect } from "react";
import { X, Calendar as CalendarIcon, CheckCircle, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import CustomSelect from "../../components/ui/CustomSelect";
import { useNotification } from "../../context/NotificationContext";
import { createEvent, updateEvent, deleteEvent } from "./calendrierAPI";
import Input from "../../components/ui/Input";
import { Button } from "../../components/ui/button";

const EVENT_TYPES = [
  { label: "Type d'événement *", value: "" },
  { label: "Appel de fonds", value: "CALL_FOR_FUNDS" },
  { label: "Échéance", value: "DEADLINE" },
  { label: "Rendez-vous", value: "MEETING" },
  { label: "Appel téléphonique", value: "CALL" },
  { label: "Visioconférence", value: "VIDEO_CONFERENCE" },
  { label: "Comité / Revue interne", value: "INTERNAL_REVIEW" }
];

export default function CalendrierForm({ open, onClose, onSaveSuccess, event }) {
  const { showToast } = useNotification();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: new Date().toISOString().slice(0, 16),
    endDate: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
    eventType: "MEETING",
    allDay: false
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        id: event.id,
        title: event.title || "",
        description: event.description || "",
        startDate: event.startDate ? new Date(event.startDate).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
        endDate: event.endDate ? new Date(event.endDate).toISOString().slice(0, 16) : new Date(Date.now() + 3600000).toISOString().slice(0, 16),
        eventType: event.eventType || "MEETING",
        allDay: event.allDay || false
      });
    } else {
      setFormData({
        title: "",
        description: "",
        startDate: new Date().toISOString().slice(0, 16),
        endDate: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
        eventType: "MEETING",
        allDay: false
      });
    }
  }, [event, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.startDate || !formData.endDate || !formData.eventType) {
      showToast("Veuillez remplir tous les champs obligatoires.", "error");
      return;
    }

    try {
      const payload = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString()
      };

      if (event?.id) {
        await updateEvent(event.id, payload);
        showToast("Événement mis à jour avec succès !", "success");
      } else {
        await createEvent(payload);
        showToast("Événement ajouté avec succès !", "success");
      }
      onSaveSuccess();
      onClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'événement:", error);
      showToast("Erreur lors de la sauvegarde.", "error");
    }
  };

  const handleDelete = async () => {
    if (!event?.id) return;
    setIsDeleting(true);
    try {
      await deleteEvent(event.id);
      showToast("Événement supprimé avec succès !", "success");
      onSaveSuccess();
      onClose();
    } catch (error) {
      console.error("Erreur lors de la suppression de l'événement:", error);
      showToast("Erreur lors de la suppression.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-lg bg-surface rounded-[var(--hp-radius-lg)] shadow-2xl flex flex-col overflow-hidden border border-[var(--hp-color-border-muted)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--hp-color-border-muted)] bg-workspace/50 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[var(--hp-info-100)] text-[var(--hp-info-700)] rounded-full">
                <CalendarIcon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-primary">
                  {event ? "Modifier l'événement" : "Nouvel Événement"}
                </h2>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5 text-subtle hover:text-primary" />
            </Button>
          </div>

          <div className="p-6 space-y-5">
            <Input
              type="text"
              name="title"
              placeholder="Titre de l'événement *"
              className="font-medium text-lg"
              value={formData.title}
              onChange={handleChange}
              required
            />

            <CustomSelect
              value={formData.eventType}
              onChange={(val) => handleSelectChange("eventType", val)}
              options={EVENT_TYPES}
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-subtle uppercase tracking-wider mb-1">
                  Début *
                </label>
                <Input
                  type={formData.allDay ? "date" : "datetime-local"}
                  name="startDate"
                  className="w-full"
                  value={formData.allDay ? formData.startDate.split('T')[0] : formData.startDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-subtle uppercase tracking-wider mb-1">
                  Fin *
                </label>
                <Input
                  type={formData.allDay ? "date" : "datetime-local"}
                  name="endDate"
                  className="w-full"
                  value={formData.allDay ? formData.endDate.split('T')[0] : formData.endDate}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="allDay"
                name="allDay"
                className="w-4 h-4 text-[var(--hp-primary-500)] border-[var(--hp-color-border)] rounded focus:ring-[var(--hp-primary-500)]"
                checked={formData.allDay}
                onChange={handleChange}
              />
              <label htmlFor="allDay" className="text-sm font-medium text-primary cursor-pointer">
                Toute la journée
              </label>
            </div>

            <textarea
              name="description"
              placeholder="Description (optionnel)..."
              className="workspace-input min-h-[100px] resize-y"
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-workspace/50 backdrop-blur-md border-t border-[var(--hp-color-border-muted)] flex justify-between gap-3">
            {event?.id ? (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Supprimer
              </Button>
            ) : (
              <div />
            )}
            
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>
                Annuler
              </Button>
              <Button variant="primary" onClick={handleSubmit}>
                <CheckCircle className="w-4 h-4 mr-1.5" />
                {event ? "Enregistrer" : "Créer l'événement"}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
